// WAAP CORS Header — free-form response-header map, via the private CDN portal:
//   GET  /cors-headers  → { "Access-Control-Allow-Origin": "...", ... }
//   POST /cors-headers  → replaces the whole map
//
// NOTE: kept on PRIVATE — the public /cors surface is currently broken by
// an SDK bug (conversant@v0.44.0): the vendor returns `accessControlMaxAge` as a
// string but WAAPCORSHeadersResponse types it as int → GET 500 "cannot unmarshal
// string into int"; PUT also 400s "AccessControlAllowCredentials is empty or
// invalid". Re-migrate to /cors once the SDK is fixed.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';

export type WaapCorsHeaders = Record<string, string>;

/** Read the website's CORS response-header map (null on 400/404). */
export function useWaapCorsHeaders(domainId: string | undefined) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', WAAP_AREAS.corsHeaders),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapCorsHeaders | null> => {
      try {
        const res = await serviceApiV3.get<WaapCorsHeaders>(waapPath(domainId as string, WAAP_AREAS.corsHeaders));
        return res.data ?? null;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

/** Replace the website's CORS response-header map. */
export function useSetWaapCorsHeaders(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, WaapCorsHeaders>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WaapCorsHeaders) => {
      const res = await serviceApiV3.post(waapPath(domainId, WAAP_AREAS.corsHeaders), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.corsHeaders) });
      options?.onSuccess?.(...args);
    },
  });
}
